/** The clicker and card mapping of a course (STORY-088), as markup only: the list and the form
 * take values and callbacks, import no API client and no query hook, and so render in a test
 * with no query client. `DeviceMapPanel` holds the data and the state. */
import { useId } from "react";
import { useTranslation } from "react-i18next";

import type { CourseDevice } from "../api/types";
import { Button } from "../ui";
import type { RosterEntry } from "./api";
import { cardStudentRow, effectiveSlot, joinsAnotherWay, refusalKey, sortDeviceRows, studentName, studentOptions, type DeviceFormValues, type MappableChannel } from "./deviceMap";

const hint = "text-xs text-[color:var(--ink-2)]";

export function DeviceMapList({
  rows,
  roster,
  guestOnly,
  onChange,
  onRemove,
}: {
  rows: CourseDevice[];
  roster: RosterEntry[];
  guestOnly: boolean;
  onChange?: (row: CourseDevice) => void;
  onRemove?: (row: CourseDevice) => void;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return <p className={hint}>{t("course.devices.empty")}</p>;
  return (
    <ul className="flex flex-col divide-y divide-[color:var(--line)]">
      {sortDeviceRows(rows, roster).map((row) => {
        const name = row.subject ? studentName(row.subject, roster) : null;
        return (
          <li key={`${row.channel}:${row.device_key}`} className="flex flex-col gap-1 py-2 text-sm">
            <p className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">
                {row.seat != null ? t("course.devices.seat", { seat: row.seat }) : name ?? t("course.devices.unnamed")}
              </span>
              <span className={hint}>{t(`live.channel.names.${row.channel}`)}</span>
              <code className="break-all font-mono text-xs" translate="no">{row.device_key}</code>
            </p>
            {row.label && <p className="break-words">{row.label}</p>}
            {cardStudentRow(row) && <p className={hint}>{t("course.devices.cardUnused")}</p>}
            {guestOnly && row.subject && !cardStudentRow(row) && <p className={hint}>{t("course.devices.studentUnused")}</p>}
            {guestOnly && row.label && <p className={hint}>{t("course.devices.noteUnused")}</p>}
            {(onChange || onRemove) && (
              <p className="flex flex-wrap gap-2">
                {onChange && (
                  <Button variant="tertiary" size="sm" aria-label={t("course.devices.changeOf", { key: row.device_key })} onClick={() => onChange(row)}>
                    {t("course.devices.change")}
                  </Button>
                )}
                {onRemove && (
                  <Button variant="tertiary" size="sm" aria-label={t("course.devices.removeOf", { key: row.device_key })} onClick={() => onRemove(row)}>
                    {t("course.devices.remove")}
                  </Button>
                )}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export type { DeviceFormValues };

export type Refusal = { code: string | null; holder?: string };

export function DeviceMapForm({
  values,
  onChange,
  roster,
  guestOnly,
  channels,
  refusal,
  pending = false,
  onSubmit,
  onCancel,
}: {
  values: DeviceFormValues;
  onChange: (next: DeviceFormValues) => void;
  roster: RosterEntry[];
  guestOnly: boolean;
  channels: MappableChannel[];
  refusal: Refusal | null;
  pending?: boolean;
  onSubmit: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const options = studentOptions(values, { roster });
  const card = values.channel === "card";
  const studentOff = card || guestOnly || options.length === 0;
  const slot = effectiveSlot(values, { guestOnly, roster });
  const set = (patch: Partial<DeviceFormValues>) => onChange({ ...values, ...patch });
  const picked = roster.find((r) => r.subject === values.subject) ?? null;
  // the refusal's sentence is read with the field it marks invalid
  const keyBad = refusal?.code === "bad_device_key";
  const seatBad = refusal?.code === "bad_seat" || refusal?.code === "seat_taken";
  return (
    <form
      className="flex flex-col gap-3 text-sm"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {channels.length > 1 && (
        <label className="flex flex-col gap-1">
          {t("course.devices.channel")}
          <select className="field" name="channel" value={values.channel} disabled={values.editing} onChange={(e) => set({ channel: e.target.value as MappableChannel })}>
            {channels.map((c) => (
              <option key={c} value={c}>{t(`live.channel.names.${c}`)}</option>
            ))}
          </select>
        </label>
      )}
      <label className="flex flex-col gap-1">
        {t("course.devices.key")}
        <input
          className="field font-mono"
          name="device-key"
          value={values.key}
          readOnly={values.editing}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          translate="no"
          inputMode={card ? "numeric" : "text"}
          maxLength={card ? 4 : undefined}
          aria-invalid={keyBad || undefined}
          aria-describedby={keyBad ? `${id}-key ${id}-refusal` : `${id}-key`}
          onChange={(e) => set({ key: e.target.value })}
        />
        <span id={`${id}-key`} className={hint}>{values.editing ? t("course.devices.keyFixed") : card ? t("course.devices.cardKeyHint") : t("course.devices.keyHint")}</span>
      </label>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium">{t("course.devices.slot")}</legend>
        <label className="flex items-center gap-2">
          <input type="radio" name={`${id}-slot`} className="h-[1.125em] w-[1.125em]" checked={slot === "seat"} onChange={() => set({ slot: "seat" })} />
          {t("course.devices.seatOption")}
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" name={`${id}-slot`} className="h-[1.125em] w-[1.125em]" checked={slot === "student"} disabled={studentOff} aria-describedby={studentOff ? `${id}-student` : undefined} onChange={() => set({ slot: "student" })} />
          {t("course.devices.studentOption")}
        </label>
        {studentOff && (
          <p id={`${id}-student`} className={hint}>
            {card ? t("course.devices.cardSeatOnly") : guestOnly ? t("course.devices.guestOnlyStudent") : t("course.devices.noNamedStudents")}
          </p>
        )}
      </fieldset>
      {slot === "seat" ? (
        <label className="flex flex-col gap-1">
          {t("course.devices.seatLabel")}
          <input className="field w-28 num" name="seat" inputMode="numeric" maxLength={4} autoComplete="off" value={values.seat} aria-invalid={seatBad || undefined} aria-describedby={seatBad ? `${id}-seat ${id}-refusal` : `${id}-seat`} onChange={(e) => set({ seat: e.target.value })} />
          <span id={`${id}-seat`} className={hint}>{t("course.devices.seatHint")}</span>
        </label>
      ) : (
        <label className="flex flex-col gap-1">
          {t("course.devices.studentLabel")}
          <select className="field" name="student" value={values.subject} onChange={(e) => set({ subject: e.target.value })}>
            <option value="">{t("course.devices.studentPick")}</option>
            {options.map((o) => (
              <option key={o.subject} value={o.subject}>{o.label ?? t("course.devices.unnamed")}</option>
            ))}
          </select>
        </label>
      )}
      {slot === "student" && joinsAnotherWay(picked) && <p className="measure rounded-[var(--radius-control)] border border-[color:var(--line)] p-2 text-xs">{t("course.devices.twice")}</p>}
      {guestOnly ? (
        <p className={hint}>{t("course.devices.guestOnlyNote")}</p>
      ) : (
        <label className="flex flex-col gap-1">
          {t("course.devices.noteLabel")}
          <input className="field" name="note" maxLength={40} autoComplete="off" value={values.note} aria-describedby={`${id}-note`} onChange={(e) => set({ note: e.target.value })} />
          <span id={`${id}-note`} className={hint}>{t("course.devices.noteHint")}</span>
        </label>
      )}
      {refusal && (
        <p id={`${id}-refusal`} role="alert" className="text-[color:var(--danger)]">
          {t(refusalKey(refusal.code), { key: refusal.holder ?? "" })}
        </p>
      )}
      <p className="flex flex-wrap gap-2">
        <Button type="submit" variant="secondary" loading={pending}>{values.editing ? t("course.devices.save") : t("course.devices.add")}</Button>
        {onCancel && <Button type="button" variant="tertiary" onClick={onCancel}>{t("course.devices.cancel")}</Button>}
      </p>
      {/* a card's mapping is read with each photo, so it reaches the lesson already running */}
      <p className={`measure ${hint}`}>{t(card ? "course.devices.cardApplies" : "course.devices.appliesNext")}</p>
      <p className={`measure ${hint}`}>{t("course.devices.keptWhereMade")}</p>
    </form>
  );
}
